import { useEffect, useState } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { ConversationPanel, type ConversationMessage } from '../../components/chow'
import { commandPayload, commands } from '../../lib/runtime'
import { compareTimestamps, formatRFC3339UTC } from '@ovasabi/frontend-kit'
import * as S from './OpsStyle'

type Row = Record<string, unknown>
const text = (row: Row, key: string) => String(row[key] ?? '')
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter((row): row is Row => !!row && typeof row === 'object') : []

const Layout = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  gap: ${minimalVars.space.sm};
  height: min(720px, 80dvh);
  @media (max-width: 767px) { grid-template-columns: minmax(0, 1fr); height: 80dvh; }
`
const Pane = styled.section<{ $hideOnMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  gap: ${minimalVars.space.xs};
  @media (max-width: 767px) { display: ${({ $hideOnMobile }) => $hideOnMobile ? 'none' : 'flex'}; }
`
const List = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${minimalVars.space.xs};
  min-width: 0;
  > button { white-space: normal; overflow-wrap: anywhere; text-align: left; flex-shrink: 0; }
`
const History = styled.div`flex: 1; min-height: 0; min-width: 0;`
const Controls = styled.div`display: flex; min-width: 0; gap: ${minimalVars.space.xs}; align-items: center; flex-wrap: wrap; > * { min-width: 0; overflow-wrap: anywhere; }`

const loadThreads = (cursor: string) => commands.message.opsListThreads(cursor)

function usePage(load: (cursor: string) => Promise<unknown>, field: string) {
  const [cursors, setCursors] = useState([''])
  const [data, setData] = useState<Row[]>([])
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  const cursor = cursors[cursors.length - 1]
  useEffect(() => {
    let active = true
    setBusy(true)
    setError('')
    void load(cursor).then((response) => {
      if (!active) return
      const payload = commandPayload(response)
      if (!Array.isArray(payload[field])) throw new Error('The server returned an incomplete conversation page.')
      setData(rows(payload[field]))
      setNext(String(payload.next_cursor ?? ''))
    }).catch((err: unknown) => {
      if (active) setError(err instanceof Error ? err.message : 'Could not load conversations.')
    }).finally(() => { if (active) setBusy(false) })
    return () => { active = false }
  }, [cursor, revision, load, field])
  return {
    data, busy, error, page: cursors.length,
    canNext: !!next && !busy && !error, canPrevious: cursors.length > 1 && !busy,
    next: () => setCursors((values) => [...values, next]),
    previous: () => setCursors((values) => values.slice(0, -1)),
    refresh: () => setRevision((value) => value + 1),
  }
}

const ThreadHistory = ({ thread, onBack }: { thread: Row; onBack: () => void }) => {
  const [load] = useState(() => (cursor: string) => commands.message.opsListMessages(text(thread, 'id'), cursor))
  const page = usePage(load, 'messages')
  const messages: ConversationMessage[] = [...page.data]
    .sort((a, b) => compareTimestamps(text(a, 'created_at'), text(b, 'created_at'), text(a, 'id'), text(b, 'id')))
    .map((message) => ({
      id: text(message, 'id'), body: text(message, 'body'), senderId: text(message, 'sender_profile_id'),
      senderLabel: text(message, 'sender_class') === 'customer' ? `${text(thread, 'customer_name')} · Customer`
        : ['chef', 'market_vendor', 'caterer'].includes(text(message, 'sender_class')) ? `${text(thread, 'chef_name')} · Chef`
          : text(message, 'sender_class'),
      createdAt: formatRFC3339UTC(text(message, 'created_at')) || undefined,
    }))
  return <Pane>
    <Controls><MinimalButton variant="secondary" onClick={onBack}>Conversations</MinimalButton><strong>{text(thread, 'subject')}</strong></Controls>
    <span>{text(thread, 'customer_name')} ↔ {text(thread, 'chef_name')} · Order {text(thread, 'order_id').slice(0, 8)}</span>
    {page.error && <MinimalAlert tone="danger" title="Could not load messages">{page.error}</MinimalAlert>}
    <Controls>
      <MinimalButton variant="secondary" disabled={!page.canPrevious} onClick={page.previous}>Newer messages</MinimalButton>
      <MinimalButton variant="secondary" disabled={!page.canNext} onClick={page.next}>Older messages</MinimalButton>
      <MinimalButton variant="secondary" disabled={page.busy} onClick={page.refresh}>Refresh messages</MinimalButton>
    </Controls>
    {page.busy ? <S.Empty role="status">Loading messages…</S.Empty> : !page.error && <History>
      <ConversationPanel messages={messages} currentActorId={text(thread, 'merchant_profile_id')} onSend={() => {}} readOnly emptyTitle="No messages yet" />
    </History>}
  </Pane>
}

export const OpsMessagesPage = () => {
  const page = usePage(loadThreads, 'threads')
  const [selected, setSelected] = useState<Row | null>(null)
  return <>
    <div><S.H1>Messages</S.H1></div>
    <Layout>
      <Pane $hideOnMobile={!!selected}>
        {page.error && <MinimalAlert tone="danger" title="Could not load conversations">{page.error}</MinimalAlert>}
        <Controls>
          <MinimalButton variant="secondary" disabled={!page.canPrevious} onClick={page.previous}>Previous</MinimalButton>
          <span>Page {page.page}</span>
          <MinimalButton variant="secondary" disabled={!page.canNext} onClick={page.next}>Next</MinimalButton>
          <MinimalButton variant="secondary" disabled={page.busy} onClick={page.refresh}>Refresh</MinimalButton>
        </Controls>
        <List aria-label="Order conversations">
          {page.busy ? <S.Empty role="status">Loading conversations…</S.Empty> : !page.error && page.data.map((thread) => <MinimalButton
            key={text(thread, 'id')} variant={text(selected ?? {}, 'id') === text(thread, 'id') ? 'primary' : 'secondary'}
            onClick={() => setSelected(thread)}>
            {text(thread, 'customer_name')} ↔ {text(thread, 'chef_name')} · {text(thread, 'subject')} · {text(thread, 'order_id').slice(0, 8)}
          </MinimalButton>)}
          {!page.busy && !page.error && page.data.length === 0 && <S.Empty>No conversations yet.</S.Empty>}
        </List>
      </Pane>
      {selected ? <ThreadHistory key={text(selected, 'id')} thread={selected} onBack={() => setSelected(null)} />
        : <Pane $hideOnMobile><S.Empty>Select a conversation to read the messages.</S.Empty></Pane>}
    </Layout>
  </>
}
